// src/components/UpgradeScreen.jsx
// Full-page screen shown when a user's trial has expired (status === 'expired').
// Hard-blocks access to the app until user upgrades via Stripe.
//
// Also reused, via an optional onClose prop, as the single consistent
// purchase screen triggered from TrialBanner.jsx during an active trial
// (both the day-1 "anytime" nudge and the 7-day warning bar). When onClose
// is passed, a "← Back" link (top) and a "Cancel" button (bottom) appear so
// the user can return to whatever page they were on. When onClose is NOT
// passed (the original post-expiry hard-block usage in App.js), neither
// appears — behavior there is byte-identical to before.
//
// SECURITY:
//   - Session fetched fresh from Supabase on mount — never passed as prop
//   - Stripe checkout initiated via StripeCheckoutButton — JWT Bearer auth
//   - No payment data handled here — Stripe hosts the payment page
//   - Error messages shown inline — no sensitive data exposed

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient.js';
import {
  StripeCheckoutButton,
  StripePaymentBanner,
  useStripeReturn,
} from './StripeCheckout.jsx';

export default function UpgradeScreen({ onLogout, userEmail, onClose, isWarning, daysLeft, expiresAt }) {
  const [session, setSession]       = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [checkoutError, setCheckoutError]   = useState(null);
  const { paymentStatus }           = useStripeReturn();
  const [bannerStatus, setBannerStatus]     = useState(paymentStatus);

  // Mutually-exclusive plan selection across Starter / Professional / QLedger.
  // selectedPlan tracks which card's country dropdown is "live" — only that
  // card shows its chosen country; every other card resets to blank the
  // moment a different card's dropdown is touched. Enterprise is excluded
  // (it's a plain "Contact Sales" link, no checkout/selection state at all).
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [country, setCountry]           = useState('');

  const handleSelectPlan = (planKey, value) => {
    setSelectedPlan(planKey);
    setCountry(value);
  };

  // Fix: if the browser restores this page from the back-forward cache
  // after the user returns from Stripe/Paddle's hosted checkout (closing
  // that tab / hitting back), clear the stale plan/country selection so
  // the cards come back to a clean, unselected state instead of showing
  // whatever was chosen right before the redirect away.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setSelectedPlan(null);
        setCountry('');
        setCheckoutError(null);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Fetch fresh Supabase session on mount — needed for Stripe Bearer token
  // .catch() ensures sessionLoading never hangs if Supabase is unreachable
  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data }) => {
        setSession(data?.session ?? null);
        setSessionLoading(false);
      })
      .catch((err) => {
        console.error('[UpgradeScreen] getSession failed:', err.message);
        setSession(null);
        setSessionLoading(false);
      });
  }, []);

  // Sync paymentStatus into dismissible bannerStatus
  useEffect(() => {
    if (paymentStatus) setBannerStatus(paymentStatus);
  }, [paymentStatus]);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '900px',
          width: '100%',
          padding: '48px 24px',
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '20px',
        }}
      >
        {/* Back link — only shown when this screen was opened voluntarily
            (TrialBanner), never during the post-expiry hard block */}
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background:   'transparent',
              color:        '#94a3b8',
              border:       'none',
              cursor:       'pointer',
              fontSize:     '14px',
              fontWeight:   600,
              padding:      0,
              marginBottom: '2px',
              display:      'flex',
              alignItems:   'center',
              gap:          '6px',
            }}
          >
            ← Back
          </button>
        )}

        {/* Payment return banner */}
        <StripePaymentBanner
          status={bannerStatus}
          onDismiss={() => setBannerStatus(null)}
        />

        {/* Header — copy depends on WHY this screen is showing, mirroring
            the exact 3 trial states used in TrialBanner.jsx:
            - onClose absent               = real post-expiry hard block.
            - onClose present + isWarning  = days 8-14 (voluntary "Purchase"
              click) — show the same countdown copy as the banner, not a
              generic "Choose Your Plan".
            - onClose present + !isWarning = days 1-7 (voluntary "Upgrade"
              click) — generic, no urgency. */}
        {(() => {
          const formattedDate = expiresAt instanceof Date
            ? expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '';

          let icon, headline, subtitle;
          if (!onClose) {
            icon = '🔒';
            headline = 'Your 14-day trial has ended';
            subtitle = 'Thanks for trying. Choose a plan to continue your compliance work and lock in your quantum-safe audit trail.';
          } else if (isWarning) {
            icon = '🔒';
            if (daysLeft === 0)      headline = 'Your trial ends today.';
            else if (daysLeft === 1) headline = 'Your trial ends tomorrow.';
            else                     headline = `Your trial ends in ${daysLeft} days (${formattedDate}).`;
            subtitle = 'Choose a plan to continue your compliance work and lock in your quantum-safe audit trail.';
          } else {
            icon = '🚀';
            headline = 'Choose Your Plan';
            subtitle = 'Upgrade anytime to unlock full access and lock in your quantum-safe audit trail.';
          }

          return (
            <div style={{ textAlign: 'center', marginBottom: '36px' }}>
              <div style={{ fontSize: '64px', marginBottom: '8px' }}>{icon}</div>
              <h1
                style={{
                  fontSize: 'clamp(22px, 3vw, 32px)',
                  fontWeight: 800,
                  margin: '0 0 12px',
                  letterSpacing: '-0.02em',
                }}
              >
                {headline}
              </h1>
              <p
                style={{
                  fontSize: 'clamp(14px, 1.3vw, 17px)',
                  color: '#94a3b8',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {' '}
                <strong style={{ color: '#22d3ee' }}>{subtitle}</strong>
              </p>
            </div>
          );
        })()}

        {/* Error message */}
        {checkoutError && (
          <div
            role="alert"
            style={{
              background:   '#2a1a0f',
              border:       '1px solid #f59e0b',
              borderRadius: '12px',
              padding:      '10px 14px',
              marginBottom: '20px',
              fontSize:     '13px',
              color:        '#fcd34d',
              textAlign:    'center',
            }}
          >
            ⚠️ {checkoutError}
          </div>
        )}

        {/* Pricing cards */}
        {sessionLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
            Loading plans…
          </div>
        ) : (
          <>
          <div
            style={{
              display:             'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap:                 '16px',
              marginBottom:        '32px',
            }}
          >
            {/* Starter */}
            <PlanCard
              name="Starter"
              price="$2,388"
              period="per year"
              monthlyEq="$199/mo"
              description="For startups getting compliant fast"
              features={['1 AI system', 'All 3 frameworks', 'Audit-ready export']}
              planKey="starter"
              session={session}
              onError={setCheckoutError}
              selectedPlan={selectedPlan}
              country={country}
              onSelectPlan={handleSelectPlan}
            />

            {/* Professional */}
            <PlanCard
              name="Professional"
              price="$4,188"
              period="per year"
              monthlyEq="$349/mo"
              description="For regulated industry deployments"
              features={['5 AI systems', 'Unlimited docs', 'KEM + API access']}
              planKey="professional"
              session={session}
              isPopular
              onError={setCheckoutError}
              selectedPlan={selectedPlan}
              country={country}
              onSelectPlan={handleSelectPlan}
            />

            {/* Enterprise */}
            <PlanCard
              name="Enterprise"
              price="Contact Us"
              period=""
              monthlyEq={null}
              description="For Fortune 5000 deployments"
              features={['Unlimited AI systems', 'Dedicated manager', '4hr SLA support', 'Annual compliance review','Volume-based AI call limits (negotiable)', 'White-glove onboarding & migration','Custom AI GRC frameworks (beyond EU AI Act/NIST/ISO 42001)']}
              isContactSales
            />

            {/* QLedger — same row as the other 3 plans */}
            <PlanCard
              name="QLedger Sealed"
              price="$5,999"
              period="one-time"
              monthlyEq={null}
              description="Quantum-Safe Compliance Package"
              features={['ISO 42001 audit-ready', 'Full doc package', 'QLedger Sealed + QR verification','Cryptographically-verifiable quantum seal' ]}
              planKey="qledger"
              session={session}
              isOneTime
              onError={setCheckoutError}
              selectedPlan={selectedPlan}
              country={country}
              onSelectPlan={handleSelectPlan}
            />
          </div>
          </>
        )}

        {/* Divider */}
        <div
          style={{
            borderTop:    '1px solid rgba(255,255,255,0.08)',
            paddingTop:   '24px',
            marginBottom: '20px',
          }}
        />

        {/* Cancel — only shown when this screen was opened voluntarily
            (TrialBanner), never during the post-expiry hard block */}
        {onClose && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button
              onClick={onClose}
              style={{
                background:   'transparent',
                color:        '#64748b',
                border:       '1px solid #475569',
                padding:      '8px 20px',
                borderRadius: '10px',
                cursor:       'pointer',
                fontSize:     '13px',
                fontWeight:   600,
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Book a call option */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 12px' }}>
            Prefer to talk first?
          </p>
          <button
            data-cal-link="quantumaishield/assess-your-quantum-risk"
            data-cal-namespace="assess-your-quantum-risk"
            style={{
              display:      'inline-block',
              padding:      '12px 28px',
              background:   'linear-gradient(to right, #00ccff, #0077ff)',
              color:        'white',
              border:       'none',
              borderRadius: '10px',
              fontWeight:   700,
              fontSize:     '14px',
              cursor:       'pointer',
              boxShadow:    '0 4px 12px rgba(0,119,255,0.3)',
            }}
          >
            📅 Book a call with us
          </button>
        </div>

        {/* Email + sign out */}
        <div style={{ textAlign: 'center' }}>
          <a
            href="mailto:support@quantumaishield.com"
            style={{ color: '#22d3ee', textDecoration: 'none', fontSize: '13px' }}
          >
            Or email support@quantumaishield.com
          </a>

          <div style={{ marginTop: '16px', fontSize: '12px', color: '#64748b' }}>
            Signed in as{' '}
            <strong style={{ color: '#94a3b8' }}>{userEmail}</strong>
            <br />
            <button
              onClick={onLogout}
              style={{
                marginTop:    '10px',
                background:   'transparent',
                color:        '#94a3b8',
                border:       '1px solid #475569',
                padding:      '7px 16px',
                borderRadius: '12px',
                cursor:       'pointer',
                fontSize:     '12px',
              }}
            >
              Sign out / use different account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PlanCard — individual pricing card with Stripe checkout button ────────────
function PlanCard({
  name, price, period, monthlyEq, description,
  features, planKey, session, isPopular, isOneTime, isContactSales, onError,
  selectedPlan, country, onSelectPlan,
}) {
  // Only the currently-selected card shows its chosen country — every other
  // card's dropdown displays blank until the user interacts with it again.
  const isSelected     = selectedPlan === planKey;
  const displayCountry = isSelected ? country : '';

  return (
    <div
      style={{
        background:    isPopular
          ? 'rgba(0,204,255,0.08)'
          : 'rgba(255,255,255,0.04)',
        border:        isPopular
          ? '2px solid #00ccff'
          : '1px solid rgba(255,255,255,0.1)',
        borderRadius:  '12px',
        padding:       '20px 16px',
        display:       'flex',
        flexDirection: 'column',
        gap:           '12px',
        position:      'relative',
      }}
    >
      {/* Popular badge */}
      {isPopular && (
        <span
          style={{
            position:      'absolute',
            top:           '-12px',
            left:          '50%',
            transform:     'translateX(-50%)',
            background:    'linear-gradient(to right, #00ccff, #0077ff)',
            color:         '#fff',
            fontSize:      '10px',
            fontWeight:    700,
            letterSpacing: '0.08em',
            padding:       '3px 12px',
            borderRadius:  '20px',
            whiteSpace:    'nowrap',
          }}
        >
          POPULAR
        </span>
      )}

      {/* Plan info */}
      <div>
        <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 4px' }}>
          {description}
        </p>
        <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: 700, margin: '0 0 6px' }}>
          {name}
        </h3>
        <p style={{ color: '#f1f5f9', fontSize: '22px', fontWeight: 800, margin: 0 }}>
          {price}
          <span style={{ fontSize: '12px', fontWeight: 400, color: '#94a3b8', marginLeft: '4px' }}>
            {period}
          </span>
        </p>
        {monthlyEq && (
          <p style={{ color: '#22c55e', fontSize: '11px', margin: '4px 0 0' }}>
            Annual only · {monthlyEq}
          </p>
        )}
        {isOneTime && (
          <p style={{ color: '#f59e0b', fontSize: '11px', margin: '4px 0 0' }}>
            ⚡ One-time — no subscription
          </p>
        )}
      </div>

      {/* Feature list */}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {features.map(f => (
          <li key={f} style={{ fontSize: '12px', color: '#cbd5e1', display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ color: '#22c55e' }}>✓</span> {f}
          </li>
        ))}
      </ul>

      {/* Stripe checkout button */}

{isContactSales ? (
        <a

      href="mailto:support@quantumaishield.com?subject=Enterprise%20Plan%20Inquiry"
          style={{
            display:        'block',
            textAlign:      'center',
            padding:        '12px 20px',
            background:     'linear-gradient(to right, #00ccff, #0077ff)',
            color:          '#ffffff',
            borderRadius:   '8px',
            textDecoration: 'none',
            fontWeight:     700,
            fontSize:       '14px',
            boxShadow:      '0 4px 12px rgba(0,119,255,0.3)',
          }}
        >
          Contact Sales
        </a>


      ) : (
        <StripeCheckoutButton
          planKey={planKey}
          session={session}
          onError={onError}
          country={displayCountry}
          onCountryChange={(value) => onSelectPlan(planKey, value)}
        >
          {isOneTime ? `Purchase ${name}` : `Upgrade to ${name}`}
        </StripeCheckoutButton>
      )}



    </div>
  );
}